import React, { useContext, useCallback } from "react";
import _ from "lodash";
import { BreakpointTrackerContext } from "@jutro/layout";
import { WizardPage, wizardProps } from "@xengage/gw-portals-wizard-react";
import { ViewModelForm } from "@xengage/gw-portals-viewmodel-react";
import { useValidation } from "@xengage/gw-portals-validation-react";
import { readViewModelValue } from "@xengage/gw-jutro-adapters-react";
import { withRouter } from "react-router-dom";

import { PremierConstants } from "premier-constant-service";
import { useUpdateSubmissionServices, validationUtil } from "premier-capability-hooks";
import presentationMetadata from "gw-capability-gateway-quoteandbind-PremierPL-react/config/PremierPL-wizard-config.json5";
import metadata from "./DirAndOffLiabilityPage.metadata.json5";
import CoveragesPageComponent from "../Coverages/CoveragesPage";
import ProductandCoverageSelectionComponent from "../../components/DirectorsAndOfficers/ProductandCoverageSelection/ProductandCoverageSelectionComponent";
import BusinessOperationsComponent from "../../components/DirectorsAndOfficers/BusinessOperations/BusinessOperationsComponent";
import RevenueComponent from "../../components/DirectorsAndOfficers/Revenue/RevenueComponent";
import EmploymentPracticesLiabilityComponent from "../../components/DirectorsAndOfficers/EmploymentPracticesLiability/EmploymentPracticesLiabilityComponent";
import CondoCorporationComponent from "../../components/DirectorsAndOfficers/CondoCorporation/CondoCorporationComponent";

function DirAndOffLiabilityPage(props) {
  const {
    wizardData: submissionVM,
    updateWizardData,
    pathUpdated,
    changeNextSteps,
    steps,
  } = props;
  const breakpoint = useContext(BreakpointTrackerContext);
  const { onValidate, initialValidation } = useValidation(
    "DirAndOffLiabilityPage"
  );
  const { updateSubmission } = useUpdateSubmissionServices();
  const { lineName } = PremierConstants;
  const propertySteps = _.filter(
    _.get(presentationMetadata, [
      `${submissionVM.value.baseData.productCode}.default.wizard`,
      "steps",
    ]),
    (wizardStep) => wizardStep.id.includes("property")
  );

  const onNext = useCallback(async () => {
    if (
      !_.isUndefined(
        submissionVM.value.lobData.proLine.coverables
          .dirAndOffProfessionalCoverable.productandCoverageSelection.pro2
      ) &&
      submissionVM.value.lobData.proLine.coverables
        .dirAndOffProfessionalCoverable.productandCoverageSelection.pro2
    ) {
      if (
        _.filter(steps, (step) => step.id.includes("property")).length === 0
      ) {
        steps.splice(5, 0, propertySteps[0], propertySteps[1]);
        changeNextSteps(steps.slice(4, 13));
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        _.filter(steps, (step) => step.id.includes("property")).length !== 0
      ) {
        changeNextSteps(
          _.filter(steps, (step) => !step.id.includes("property")).slice(4, 12)
        );
      }
    }

    const productCode = _.get(submissionVM.value, "baseData.productCode");
    // eslint-disable-next-line security/detect-object-injection
    const lobLineName = productCode ? lineName[productCode] : "";
    const response = await updateSubmission(
      lobLineName,
      "DirAndOffLiabilityPage",
      submissionVM
    );

    submissionVM.value = response;

    return submissionVM;
  }, [
    changeNextSteps,
    lineName,
    propertySteps,
    steps,
    submissionVM,
    updateSubmission,
  ]);

  const onSave = useCallback(
    async () => {
      const subVMCopy = _.clone(submissionVM)
      const productCode = _.get(subVMCopy.value, 'baseData.productCode');

      validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.proLine.coverables.dirAndOffProfessionalCoverable.value, ' ')

      const lobLineName = productCode ? lineName[productCode] : '';
      await updateSubmission(lobLineName, 'DirAndOffLiabilityPage', subVMCopy);

      return submissionVM;
    },
    [submissionVM]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overrideProps = {
    "@field": {
      showRequired: true,
      labelPosition: breakpoint === "desktop" ? "left" : "top",
    },
    productandCoverageSelectionComponent: {
      submissionVM,
      updateWizardData,
      pathUpdated:
        // eslint-disable-next-line no-secrets/no-secrets
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.productandCoverageSelection",
    },
    businessOperationsComponent: {
      submissionVM,
      updateWizardData,
      pathUpdated:
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.businessOperations",
    },
    revenueComponent: {
      submissionVM,
      updateWizardData,
      pathUpdated:
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.revenue",
    },
    employmentPracticesLiabilityComponent: {
      submissionVM,
      updateWizardData,
      pathUpdated:
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.employmentPracticesLiability",
      visible: _.get(
        submissionVM,
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.productandCoverageSelection.doYouReqEmpPraLia5.value",
        false
      ),
    },
    condoCorporationComponent: {
      visible: _.get(
        submissionVM,
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.productandCoverageSelection.isThiAConStrCor6.value",
        false
      ),
      submissionVM,
      updateWizardData,
      pathUpdated:
        "lobData.proLine.coverables.dirAndOffProfessionalCoverable.condoCorporation",
      onValidate,
    },
  };

  const resolvers = {
    resolveCallbackMap: {
      onValidate,
    },
    resolveComponentMap: {
      productandCoverageSelectionComponent:
        ProductandCoverageSelectionComponent,
      businessOperationsComponent: BusinessOperationsComponent,
      revenueComponent: RevenueComponent,
      employmentPracticesLiabilityComponent:
        EmploymentPracticesLiabilityComponent,
      condoCorporationComponent: CondoCorporationComponent,
      coveragespagecomponent: CoveragesPageComponent,
    },
  };

  const readValue = useCallback(
    (id, path) =>
      readViewModelValue(
        metadata.componentContent,
        _.get(submissionVM, pathUpdated),
        id,
        path,
        overrideProps
      ),
    [overrideProps, pathUpdated, submissionVM]
  );

  const isDisableNext = () =>
    !submissionVM.lobData.proLine.coverables.dirAndOffProfessionalCoverable
      .aspects.subtreeValid;

  return (
    <WizardPage
      onNext={onNext}
      onSave={onSave}
      skipWhen={initialValidation}
      disableNext={isDisableNext()}
    >
      <ViewModelForm
        uiProps={metadata.pageContent}
        model={submissionVM}
        onModelChange={updateWizardData}
        overrideProps={overrideProps}
        onValidationChange={onValidate}
        resolveValue={readValue}
        callbackMap={resolvers.resolveCallbackMap}
        componentMap={resolvers.resolveComponentMap}
        classNameMap={resolvers.resolveClassNameMap}
      />
    </WizardPage>
  );
}

DirAndOffLiabilityPage.propTypes = wizardProps;
export default withRouter(DirAndOffLiabilityPage);
